import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CalendarClock, Download, Plus, Search, Loader2, RotateCcw, Trash2,
  AlertTriangle, Package, Truck, MapPin, CheckCircle2,
} from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';

interface Merchant { id: number; companyName: string; brandName?: string }
interface Appt {
  id: number; lrNo: string; merchantId?: number;
  merchant?: { id: number; companyName: string; brandName?: string };
  appointmentDate?: string; appointmentTime?: string; poNumber?: string; poExpiryDate?: string;
  appointmentRef?: string; statusId?: number | null; statusLabel?: string | null;
  notDelivered?: boolean; poExpired?: boolean; urgent?: boolean; createdAt?: string;
}
interface Summary { expiredYesterday: number; expiringToday: number; expiringTomorrow: number; urgentNotDelivered: number }

const fmtDate = (d?: string | null) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

export function AppointmentsPage() {
  const qc = useQueryClient();
  const [merchantId, setMerchantId] = useState('');
  const [lrNo, setLrNo] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [showBook, setShowBook] = useState(false);

  const { data: merchantsData } = useQuery({
    queryKey: ['merchants-light'],
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchants: Merchant[] = merchantsData?.items ?? merchantsData ?? [];

  const { data: summary } = useQuery<Summary>({
    queryKey: ['appointments-summary'],
    queryFn: async () => (await api.get('/appointments/summary')).data,
  });

  const filters = { merchantId, lrNo, poNumber, startDate, endDate };
  const { data: list, isLoading } = useQuery({
    queryKey: ['appointments', { ...filters, page }],
    queryFn: async () => {
      const params: any = { page, pageSize };
      if (merchantId) params.merchantId = merchantId;
      if (lrNo) params.lrNo = lrNo;
      if (poNumber) params.poNumber = poNumber;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      return (await api.get<{ total: number; items: Appt[] }>('/appointments', { params })).data;
    },
  });

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/appointments/${id}`)).data,
    onSuccess: () => { toast.success('Appointment deleted'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Delete failed'),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ['appointments'] });
    qc.invalidateQueries({ queryKey: ['appointments-summary'] });
  }
  function reset() { setMerchantId(''); setLrNo(''); setPoNumber(''); setStartDate(''); setEndDate(''); setPage(1); }

  function exportCsv() {
    const p = new URLSearchParams();
    if (merchantId) p.set('merchantId', merchantId);
    if (lrNo) p.set('lrNo', lrNo);
    if (poNumber) p.set('poNumber', poNumber);
    if (startDate) p.set('startDate', startDate);
    if (endDate) p.set('endDate', endDate);
    const qsp = p.toString();
    downloadBlob('/appointments/export' + (qsp ? `?${qsp}` : ''), `appointments-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  const total = list?.total ?? 0;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Appointments</h1>
          <p className="text-sm text-slate-500">Book &amp; track B2B delivery appointments and PO expiry against each LR</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setShowBook(true)} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90">
            <Plus size={14} /> Book an Appointment
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <SummaryTile label="PO Expired Yesterday" value={summary?.expiredYesterday} tone="amber" />
        <SummaryTile label="PO Expiring Today" value={summary?.expiringToday} tone="blue" />
        <SummaryTile label="PO Expiring Tomorrow" value={summary?.expiringTomorrow} tone="slate" />
        <SummaryTile label="Not Delivered & PO Expired" value={summary?.urgentNotDelivered} tone="red" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Merchant</label>
            <select value={merchantId} onChange={(e) => { setMerchantId(e.target.value); setPage(1); }} className="input w-full">
              <option value="">All merchants</option>
              {merchants.map(m => (<option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">LR Number</label>
            <input value={lrNo} onChange={(e) => { setLrNo(e.target.value); setPage(1); }} placeholder="Search LR…" className="input w-full font-mono" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">PO Number</label>
            <input value={poNumber} onChange={(e) => { setPoNumber(e.target.value); setPage(1); }} placeholder="Search PO…" className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appt From</label>
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appt To</label>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} className="input w-full" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={reset} className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs flex items-center gap-1.5"><RotateCcw size={12} /> Reset</button>
          <div className="ml-auto text-xs text-slate-500">{total.toLocaleString('en-IN')} appointments</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2.5">LR No</th>
                <th className="text-left px-3 py-2.5">Merchant</th>
                <th className="text-left px-3 py-2.5">Appt Date</th>
                <th className="text-left px-3 py-2.5">Time</th>
                <th className="text-left px-3 py-2.5">PO Number</th>
                <th className="text-left px-3 py-2.5">PO Expiry</th>
                <th className="text-left px-3 py-2.5">Appointment ID</th>
                <th className="text-left px-3 py-2.5">Order Status</th>
                <th className="text-center px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={9} className="text-center py-10 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>)}
              {!isLoading && (list?.items ?? []).length === 0 && (<tr><td colSpan={9} className="text-center py-12 text-slate-400">No appointments for these filters.</td></tr>)}
              {(list?.items ?? []).map(r => (
                <tr key={r.id} className={`border-t border-slate-100 ${r.urgent ? 'bg-red-50 hover:bg-red-100/70' : 'hover:bg-slate-50/60'}`}>
                  <td className="px-3 py-2 font-mono font-medium text-slate-900">
                    {r.urgent && <AlertTriangle size={13} className="inline mr-1 text-red-600" />}{r.lrNo}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.merchant?.companyName ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(r.appointmentDate)}</td>
                  <td className="px-3 py-2 text-xs">{r.appointmentTime || '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.poNumber || '—'}</td>
                  <td className={`px-3 py-2 text-xs ${r.poExpired ? 'text-red-600 font-medium' : ''}`}>{fmtDate(r.poExpiryDate)}</td>
                  <td className="px-3 py-2 text-xs">{r.appointmentRef || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.statusLabel
                      ? <span className={`px-1.5 py-0.5 rounded text-[11px] ${r.notDelivered ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{r.statusLabel}</span>
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => { if (confirm(`Delete appointment for LR ${r.lrNo}?`)) del.mutate(r.id); }}
                      className="text-slate-400 hover:text-red-600 p-1" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-600">
            <div>Page {page} of {Math.max(1, Math.ceil(total / pageSize))}</div>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Previous</button>
              <button disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      <BookAppointmentModal isOpen={showBook} onClose={() => setShowBook(false)} onSaved={refresh} />
    </div>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value?: number; tone: 'amber' | 'blue' | 'slate' | 'red' }) {
  const tones: Record<string, string> = {
    amber: 'from-amber-50 to-orange-50 border-amber-200 text-amber-900',
    blue: 'from-blue-50 to-sky-50 border-blue-200 text-blue-900',
    slate: 'from-slate-50 to-slate-100 border-slate-200 text-slate-800',
    red: 'from-red-50 to-rose-50 border-red-200 text-red-900',
  };
  return (
    <div className={`bg-gradient-to-r border rounded-xl p-4 ${tones[tone]}`}>
      <div className="text-2xl font-bold">{value ?? '—'}</div>
      <div className="text-xs font-medium mt-0.5 flex items-center gap-1">
        {tone === 'red' && <AlertTriangle size={12} />}{label}
      </div>
    </div>
  );
}

// ============ Book / Edit Appointment modal (reused on the B2B order detail page) ============
export function BookAppointmentModal({
  isOpen, onClose, lockedLrNo, onSaved,
}: { isOpen: boolean; onClose: () => void; lockedLrNo?: string; onSaved?: () => void }) {
  const [lrNo, setLrNo] = useState(lockedLrNo ?? '');
  const [card, setCard] = useState<any>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'found' | 'notfound'>('idle');
  const [form, setForm] = useState({ appointmentDate: '', appointmentTime: '', poNumber: '', poExpiryDate: '', appointmentRef: '' });
  const [pushToCourier, setPushToCourier] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setLrNo(lockedLrNo ?? '');
    setCard(null); setStatus('idle');
    setPushToCourier(true);
    setForm({ appointmentDate: '', appointmentTime: '', poNumber: '', poExpiryDate: '', appointmentRef: '' });
    if (lockedLrNo) doLookup(lockedLrNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lockedLrNo]);

  async function doLookup(lr: string) {
    const v = (lr || '').trim();
    if (!v) { setCard(null); setStatus('idle'); return; }
    setStatus('loading');
    try {
      const r = await api.get('/appointments/order-lookup', { params: { lrNo: v } });
      if (r.data?.found) { setCard(r.data.order); setStatus('found'); }
      else { setCard(null); setStatus('notfound'); }
    } catch { setCard(null); setStatus('notfound'); }
  }

  const save = useMutation({
    mutationFn: async () =>
      (await api.post('/appointments', { lrNo: lrNo.trim(), ...form, pushToCourier: pushToCourier && !!card?.supportsCourierBooking })).data,
    onSuccess: (d: any) => {
      const cb = d?.courierBooking;
      // The row is saved either way, so a carrier failure is a WARNING, not an error — saying
      // "Save failed" would send ops back to re-enter an appointment that is already on file.
      if (cb && cb.ok === false) {
        toast.warning(`Appointment saved, but ${card?.courier?.name ?? 'the courier'} did not accept it: ${cb.errorMessage ?? 'unknown error'}`, { duration: 12000 });
      } else if (cb && cb.ok) {
        toast.success(cb.appointmentId ? `Appointment booked with the courier (ref ${cb.appointmentId})` : 'Appointment booked with the courier');
      } else {
        toast.success('Appointment saved');
      }
      onSaved?.(); onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  // Delhivery rejects a booking with no PO expiry, so don't let the request leave without one.
  const pushNeedsPo = pushToCourier && !!card?.supportsCourierBooking && (!form.poExpiryDate || !form.appointmentTime);
  const canSave = status === 'found' && !!form.appointmentDate && !pushNeedsPo && !save.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Book an Appointment" subtitle="Enter the LR number, confirm the shipment, then fill the appointment details" maxWidth="2xl">
      <div className="space-y-5">
        {/* LR entry */}
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">LR Number *</label>
          <div className="flex gap-2">
            <input
              value={lrNo}
              onChange={(e) => setLrNo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doLookup(lrNo); }}
              onBlur={() => { if (!lockedLrNo) doLookup(lrNo); }}
              readOnly={!!lockedLrNo}
              placeholder="Enter LR number and press Enter"
              className={`input flex-1 font-mono ${lockedLrNo ? 'bg-slate-100' : ''}`}
            />
            {!lockedLrNo && (
              <button onClick={() => doLookup(lrNo)} disabled={!lrNo.trim() || status === 'loading'}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
                {status === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Fetch
              </button>
            )}
          </div>
          {status === 'notfound' && <div className="text-xs text-amber-600 mt-1">No order found for this LR number.</div>}
        </div>

        {/* Basic info card */}
        {status === 'found' && card && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Package size={14} /> {card.orderNo} · {card.orderType}</div>
              <span className={`text-[11px] px-2 py-0.5 rounded ${card.notDelivered ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {card.notDelivered ? <AlertTriangle size={10} className="inline mr-0.5" /> : <CheckCircle2 size={10} className="inline mr-0.5" />}{card.statusLabel}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-slate-600">
              <Info label="Merchant" value={card.merchant?.companyName} />
              <Info label="Courier" value={card.courier?.name} icon={<Truck size={11} />} />
              <Info label="AWB" value={card.awbNo} />
              <Info label="Consignee" value={card.customerName} />
              <Info label="Destination" value={[card.customerCity, card.customerState].filter(Boolean).join(', ')} icon={<MapPin size={11} />} />
              <Info label="Boxes" value={card.totalBoxes != null ? String(card.totalBoxes) : undefined} />
            </div>
          </div>
        )}

        {/* Appointment fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment Date *</label>
            <input type="date" value={form.appointmentDate} onChange={(e) => setForm(f => ({ ...f, appointmentDate: e.target.value }))} className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment Time</label>
            <input type="time" value={form.appointmentTime} onChange={(e) => setForm(f => ({ ...f, appointmentTime: e.target.value }))} className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">PO Number</label>
            <input value={form.poNumber} onChange={(e) => setForm(f => ({ ...f, poNumber: e.target.value }))} placeholder="Purchase order no." className="input w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">PO Expiry Date</label>
            <input type="date" value={form.poExpiryDate} onChange={(e) => setForm(f => ({ ...f, poExpiryDate: e.target.value }))} className="input w-full" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Appointment ID</label>
            <input value={form.appointmentRef} onChange={(e) => setForm(f => ({ ...f, appointmentRef: e.target.value }))} placeholder="Leave blank for a new booking; enter an existing ID to re-book that slot" className="input w-full" />
          </div>
        </div>

        {/* Push to the carrier. Offered only where an appointment API exists (Delhivery B2B today) —
            elsewhere this screen is our own tracker and there is nobody to send the slot to. */}
        {status === 'found' && card?.supportsCourierBooking && (
          <label className="flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={pushToCourier} onChange={(e) => setPushToCourier(e.target.checked)} />
            <span className="text-xs text-indigo-900">
              <span className="font-medium">Also book this with {card?.courier?.name ?? 'the courier'}</span>
              <span className="block text-indigo-700 mt-0.5">
                Sends the slot to their appointment API. PO Number and <strong>PO Expiry Date are required</strong> by
                Delhivery — without them they refuse the booking. The appointment is saved here either way.
              </span>
            </span>
          </label>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          {pushNeedsPo && (
            <span className="text-xs text-amber-700 mr-auto">
              <span className="font-semibold">Needed to book with the courier:</span>{' '}
              {[!form.appointmentTime && 'Appointment Time', !form.poExpiryDate && 'PO Expiry Date'].filter(Boolean).join(', ')}
              {' '}— or untick the courier booking to save locally only.
            </span>
          )}
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">CANCEL</button>
          <button onClick={() => save.mutate()} disabled={!canSave}
            className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} Save Appointment
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Info({ label, value, icon }: { label: string; value?: string; icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-slate-400">{label}:</span>
      <span className="font-medium text-slate-700 flex items-center gap-1 truncate">{icon}{value || '—'}</span>
    </div>
  );
}
